import React from 'react';
import PropTypes from 'prop-types';
import 'react-jplaylist/dist/css/skins/sleek.min.css';
import { BrowserUnsupported } from 'react-jplayer';
import JPlaylist from 'react-jplaylist';
import classNames from 'classnames';
import PlayerControl from './PlayerControl';
// we use a custom video component with old jPlayer and jPlaylist libs
// as only old libs work with react 16 and react-redux 6 + without any trouble
import Video from '../../../3p/react-jplayer/video';
import ProgressBar from './ProgressBar';
import PlaylistButtons from './PlaylistButtons';
import {
    CONTROL_TYPE,
    PLAYLIST_START_INDEX,
    JPLAYERS_TYPE,
    DEMO_VIDEOS_PROGRESS_TYPE,
    JPLAYLISTS_TYPE,
    PLAYLISTS,
} from './demoVideoConstants';
import { shouldPlayNextVideo } from './demoVideoUtils';
import { isSafari } from '../../utils/browserUtils';
import { PLAYLIST_ITEM_SHAPE } from '../../../constants/measure';

const { PLAY, PAUSE, REPEAT } = CONTROL_TYPE;
const MAX_SINGLE_DIGIT = 9;

// we don't refactor this component to be up to date with react 16 until we use another video player library
class DemoVideoView extends React.Component {
    constructor(props) {
        super(props);
        this.toggleButtonPulsating = this.toggleButtonPulsating.bind(this);
        this.setControlTypeAndShow = this.setControlTypeAndShow.bind(this);
        this.state = {
            show: this.props.jPlayers[this.props.id].paused,
            controlType: PLAY,
            isPlayButtonPulsating: false,
            demoLengths: {},
        };
    }

    componentDidMount() {
        this.props
            .narrateScreen(this.props.isExternalURLSourcePresent, this.props.arLanguage)
            .then(() => {
                // On safari browser auto video playing is blocked in case if browser settings auto-play option is different from `Allow all auto-play`
                // we dont autoplay on Safari browser, student should manually press play button
                if (isSafari()) {
                    this.setState({ isPlayButtonPulsating: true });
                }

                if (this.props.isVideoWatchedInPreviousSession) {
                    this.props.onSuccess();
                } else if (!isSafari()) {
                    this.props.playPlaylist(PLAYLIST_START_INDEX);
                }
            });
        this.getDemoDurations();
        this.props.trackStartStep();
    }

    componentDidUpdate(prevProps) {
        const {
            id,
            jPlayers,
            onSuccess,
            playPlaylist,
            activeVideoId,
            demoVideosProgress,
        } = prevProps;

        const { trackPauseDemoVideo, isExternalURLSourcePresent } = this.props;

        const prevPauseState = jPlayers[id].paused;
        const nextDuration = this.props.jPlayers[this.props.id].duration;
        const nextPauseState = this.props.jPlayers[this.props.id].paused;
        const nextCurrentTime = this.props.jPlayers[this.props.id].currentTime;
        const { jPlaylists, id: currentPlaylistId, activeVideoId: currentVideoId } = this.props;
        const currentPlaylist = jPlaylists[currentPlaylistId].playlist;
        const currentVideo = currentPlaylist.find(video => video.demoVideoId === currentVideoId);
        const nextVideoIndex = currentVideo.shufflePosition + 1;
        const hasNextVideo = currentPlaylist.length > nextVideoIndex;

        const shouldShowRepeat =
            !hasNextVideo && nextCurrentTime !== 0 && nextCurrentTime === nextDuration;

        if (nextPauseState !== prevPauseState) {
            if (isSafari()) {
                // setState is not allowed to use in componentDidUpdate, set the reuqired field
                // In next method
                this.toggleButtonPulsating(false);
            }

            if (
                shouldPlayNextVideo(nextPauseState, demoVideosProgress, activeVideoId) &&
                hasNextVideo
            ) {
                playPlaylist(nextVideoIndex);
            } else {
                let controlType = nextPauseState ? PLAY : PAUSE;

                if (shouldShowRepeat) {
                    controlType = REPEAT;
                    onSuccess();
                }
                // Use method to skip setState in componentDidUpdate
                this.setControlTypeAndShow(controlType, shouldShowRepeat || nextPauseState);
                if (
                    !isExternalURLSourcePresent &&
                    !shouldShowRepeat &&
                    !prevPauseState &&
                    nextPauseState
                )
                    trackPauseDemoVideo();
            }
            this.props.setOwlyCanSpeak(nextPauseState);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    toggleButtonPulsating = isPlayButtonPulsating => this.setState({ isPlayButtonPulsating });

    setControlTypeAndShow = (controlType, show) => this.setState({ controlType, show });

    isPlaylistInfoComplete = () => {
        const { jPlaylists, nextIncompletePlaylistItem } = this.props;
        return jPlaylists && nextIncompletePlaylistItem && nextIncompletePlaylistItem.measure;
    };

    onCanPlayThrough = audioElem => {
        this.setState(prevState => {
            return {
                ...prevState,
                demoLengths: {
                    ...prevState.demoLengths,
                    [audioElem.src]: audioElem.duration,
                },
            };
        });
        audioElem.remove();
    };

    createAudioElement = video => {
        const link = video.sources.m4v;
        const element = document.createElement('audio');
        element.src = link;
        element.addEventListener('canplaythrough', () => this.onCanPlayThrough(element));
        return element;
    };

    getDemoDurations = () => {
        const { jPlaylists, nextIncompletePlaylistItem } = this.props;
        if (this.isPlaylistInfoComplete()) {
            const playlistId = PLAYLISTS[nextIncompletePlaylistItem.measure];
            if (jPlaylists[playlistId]) {
                const { playlist } = jPlaylists[playlistId];
                playlist.forEach(video => this.createAudioElement(video));
            }
        }
    };

    getTotalDuration = () => {
        let totalSecs = 0.0;
        const { demoLengths } = this.state;
        const clips = Object.values(demoLengths);
        clips.forEach(clip => {
            totalSecs += clip;
        });
        // Calculate total minutes and seconds
        const minutes = Math.floor(totalSecs / 60);
        const seconds = Math.floor(totalSecs % 60);
        return `${minutes}:${seconds > MAX_SINGLE_DIGIT ? seconds : `0${seconds}`}`;
    };

    playPlaylistWithTracking = playlistArgs => {
        const { playPlaylist, trackRestartDemoVideo, isExternalURLSourcePresent } = this.props;
        const { show, controlType } = this.state;
        if (!isExternalURLSourcePresent && controlType === REPEAT && show) trackRestartDemoVideo();
        playPlaylist(playlistArgs);
    };

    render() {
        const {
            activeVideoId,
            demoVideosProgress,
            playPlaylist,
            id: playlistId,
            jPlaylists,
            events,
        } = this.props;
        const { show, controlType, isPlayButtonPulsating } = this.state;
        const demoDuration = this.getTotalDuration();

        return (
            <JPlaylist>
                <div className={classNames('video-container', activeVideoId)}>
                    <PlayerControl
                        show={show}
                        large={true}
                        type={controlType}
                        playPlaylist={this.playPlaylistWithTracking}
                        pulsating={isPlayButtonPulsating}
                    />
                    <Video id="video" events={events} />
                </div>
                <ProgressBar
                    controlType={controlType}
                    playPlaylist={playPlaylist}
                    activeVideoId={activeVideoId}
                    demoVideosProgress={demoVideosProgress}
                    demoVideosDuration={demoDuration}
                    playlist={jPlaylists[playlistId]}
                    playPlaylistWithTracking={this.playPlaylistWithTracking}
                />
                <BrowserUnsupported />
                <PlaylistButtons
                    playPlaylist={playPlaylist}
                    activeVideoId={activeVideoId}
                    demoVideosProgress={demoVideosProgress}
                    playlist={jPlaylists[playlistId]}
                />
            </JPlaylist>
        );
    }
}

DemoVideoView.propTypes = {
    events: PropTypes.object,
    activeVideoId: PropTypes.string,
    id: PropTypes.string.isRequired,
    cleanup: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    jPlayers: JPLAYERS_TYPE.isRequired,
    playPlaylist: PropTypes.func.isRequired,
    narrateScreen: PropTypes.func.isRequired,
    demoVideosProgress: DEMO_VIDEOS_PROGRESS_TYPE.isRequired,
    isVideoWatchedInPreviousSession: PropTypes.bool.isRequired,
    setOwlyCanSpeak: PropTypes.func,
    jPlaylists: JPLAYLISTS_TYPE.isRequired,
    nextIncompletePlaylistItem: PLAYLIST_ITEM_SHAPE.isRequired,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    trackStartStep: PropTypes.func,
    trackPauseDemoVideo: PropTypes.func,
    trackRestartDemoVideo: PropTypes.func,
    arLanguage: PropTypes.string.isRequired,
};

DemoVideoView.defaultProps = {
    activeVideoId: '',
    events: {},
    setOwlyCanSpeak: () => {},
    onSuccess: () => {},
    trackStartStep: () => {},
    trackPauseDemoVideo: () => {},
    trackRestartDemoVideo: () => {},
};

export default DemoVideoView;
